import React, { useEffect, useState } from 'react';
import { MatchDetail, Language } from '../../types';
import { SoccerBallIcon } from './MatchIcons';
import { Heart } from 'lucide-react';
import { getTeamName } from '../../services/teamService';
import { t } from '../../i18n';
import { favoriteService } from '../../services/favoriteService';
import { useAuth } from '../../context/AuthContext';
import { ConfirmModal } from '../Common/ConfirmModal';
import { getWeatherForMatch, WeatherInfo } from '../../services/weatherService';

interface MatchScoreHeaderProps {
    detail: MatchDetail;
    lang: Language;
    homeForm: string[];
    awayForm: string[];
    onOpenVenue: () => void;
    onTeamClick?: (team: { id: string; name: string; logo: string }) => void;
    onRefresh?: () => void;
    isRefreshing?: boolean;
    onToggleFavorite?: (teamId: string) => void;
    onOpenStandings?: () => void;
}

const TeamFavoriteButton: React.FC<{ teamId: string; lang: Language; onToggle?: (teamId: string) => void }> = ({ teamId, lang, onToggle }) => {
    const { user } = useAuth();
    const [isFavorite, setIsFavorite] = useState(false);
    const [isRegistered, setIsRegistered] = useState(false);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const checkStatus = async () => {
            if (user && teamId) {
                const results = await favoriteService.getFavoriteTeams();
                const teamSelection = results.find(r => r.team_id === teamId);
                setIsRegistered(!!teamSelection);
                setIsFavorite(teamSelection?.is_favorite || false);
            }
        };
        checkStatus();
    }, [user, teamId]);

    const handleToggle = async (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!user || !teamId || loading) return;

        if (isRegistered) {
            setLoading(true);
            const success = await favoriteService.removeFavorite(teamId);
            if (success) {
                setIsRegistered(false);
                setIsFavorite(false);
            }
            setLoading(false);
        } else if (onToggle) {
            onToggle(teamId);
        }
    };

    if (!user || !teamId) return null;

    return (
        <button
            onClick={handleToggle}
            className={`absolute -top-1 -right-1 w-6 h-6 rounded-full bg-white shadow-md flex items-center justify-center border transition-all active:scale-90 z-20 ${
                isRegistered ? 'text-rose-500 border-rose-100' : 'text-slate-300 border-gray-100'
            }`}
        >
            <Heart size={12} fill={isRegistered ? 'currentColor' : 'none'} />
        </button>
    );
};

const TeamFormStrip: React.FC<{ form?: string[] }> = ({ form }) => {
    if (!form || form.length === 0) return null;
    return (
        <div className="flex gap-1 mt-2">
            {form.map((res, i) => {
                const color = res === 'W' ? 'bg-emerald-500' : res === 'D' ? 'bg-slate-300' : 'bg-rose-500';
                return (
                    <div 
                        key={i} 
                        className={`w-3.5 h-3.5 rounded-full flex items-center justify-center text-[7px] font-black text-white ${color}`}
                        title={res === 'W' ? 'Win' : res === 'D' ? 'Draw' : 'Loss'}
                    >
                        {res}
                    </div>
                );
            })}
        </div>
    );
};

export const MatchScoreHeader: React.FC<MatchScoreHeaderProps> = ({ detail, lang, homeForm, awayForm, onOpenVenue, onTeamClick, onRefresh, isRefreshing, onToggleFavorite, onOpenStandings }) => {
    const isScheduled = detail.status === 'Scheduled';
    const [weather, setWeather] = useState<WeatherInfo | null>(null);

    useEffect(() => {
        const fetchWeather = async () => {
            if (!detail.homeTeam?.name || !detail.dateLabel) return;
            
            const matchDateStr = detail.dateLabel.replace(/\./g, '-');
            // 로컬 타임존 기준으로 날짜만 가져와서 비교하도록 처리
            const [year, month, day] = matchDateStr.split('-').map(Number);
            const matchDate = new Date(year, month - 1, day);
            if (isNaN(matchDate.getTime())) return;

            const now = new Date();
            const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
            
            const diffTime = today.getTime() - matchDate.getTime();
            const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));
            
            // diffDays === 0 (오늘)만 노출
            if (diffDays <= 0) {
                let city = detail.homeTeam.name;
                
                if (detail.venueCity) {
                    city = detail.venueCity;
                } else if (typeof detail.venue === 'string' && detail.venue.includes(',')) {
                    city = detail.venue.split(',')[1].trim();
                }

                const w = await getWeatherForMatch(city);
                setWeather(w);
            }
        };
        fetchWeather();
    }, [detail.homeTeam?.name, detail.dateLabel, detail.venue]);

    const handleHomeClick = () => {
        if (onTeamClick) onTeamClick({ id: detail.homeTeam.id || '', name: detail.homeTeam.name, logo: detail.homeTeam.logo });
    };

    const handleAwayClick = () => {
        if (onTeamClick) onTeamClick({ id: detail.awayTeam.id || '', name: detail.awayTeam.name, logo: detail.awayTeam.logo });
    };

    return (
        <div className="relative bg-white px-0 z-10">
            <div className="relative z-10 px-6 py-6">
                <div className="bg-white text-slate-900">
                    <div className="flex justify-between items-center relative">
                        <div className="flex flex-col items-center w-[35%]">
                            <div className="relative">
                                <div 
                                    className="w-16 h-16 mb-2 p-3 bg-gray-50 rounded-2xl flex items-center justify-center border border-gray-100 group transition-transform md:hover:scale-110 cursor-pointer shadow-sm md:hover:shadow-md" 
                                    onClick={handleHomeClick}
                                >
                                    <img src={detail.homeTeam.logo} alt="" className="w-full h-full object-contain" />
                                </div>
                                {detail.homeTeam.id && <TeamFavoriteButton teamId={detail.homeTeam.id} lang={lang} onToggle={onToggleFavorite} />}
                            </div>
                            <h3 className="font-black text-xs text-center uppercase tracking-tight line-clamp-2 text-slate-800 h-8 flex items-center cursor-pointer md:hover:text-rose-600" onClick={handleHomeClick}>{getTeamName(detail.homeTeam.id, lang, detail.homeTeam.name)}</h3>
                            <TeamFormStrip form={homeForm} />
                        </div>
                        <div className="flex flex-col items-center justify-center w-[30%]">
                            
                            {weather && (
                                <div className="flex items-center gap-1 text-slate-700 px-3 text-xs font-black">
                                    <img 
                                        src={`https://openweathermap.org/img/wn/${weather.icon}@2x.png`} 
                                        alt={weather.description} 
                                        className="w-8 h-8 object-contain drop-shadow-sm"
                                    />
                                    <span>{weather.temp}°C</span>
                                </div>
                            )}

                            {isScheduled ? (
                                <div className="text-3xl font-black text-rose-500 tabular-nums">{detail.time}</div>
                            ) : (
                                <>
                                    <div className="text-5xl font-black tracking-tighter flex gap-2 text-slate-900 tabular-nums">
                                        <span>{detail.goals?.home ?? 0}</span>
                                        <span className="text-rose-500 animate-pulse">:</span>
                                        <span>{detail.goals?.away ?? 0}</span>
                                    </div>
                                    <div className="mt-2 flex flex-col items-center gap-1">
                                        {detail.scoreDetails?.halftime && detail.scoreDetails.halftime.home !== null && (
                                            <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest bg-gray-50 px-2 py-0.5 rounded-md">
                                                HT {detail.scoreDetails.halftime.home}:{detail.scoreDetails.halftime.away}
                                            </span>
                                        )}
                                        {detail.scoreDetails?.penalty && detail.scoreDetails.penalty.home !== null && (
                                            <span className="text-[10px] font-black text-white bg-rose-500 px-2 py-0.5 rounded-md animate-in zoom-in duration-500">
                                                (PEN) {detail.scoreDetails.penalty.home}:{detail.scoreDetails.penalty.away}
                                            </span>
                                        )}
                                    </div>
                                </>
                            )}
                            {!isScheduled && detail.status !== 'Scheduled' && (
                                <div className="mt-3 flex flex-col items-center gap-2">
                                    <div className="bg-slate-900 text-white px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-[0.2em]">
                                        {detail.status === 'Finished' ? t(lang, 'fullTime') : 
                                         detail.status === 'Live' ? (detail.elapsed ? `${detail.elapsed}'` : detail.currentMinute ? `${detail.currentMinute}'` : 'Live') : 
                                         detail.status}
                                    </div>
                                </div>
                            )}
                            <div className="mt-2 flex items-center">
                                <span className="text-[11px] font-black text-slate-400 tracking-tight text-center">
                                    {detail.dateLabel?.replace(/-/g, '.') || ''} {detail.time || ''}
                                </span>
                            </div>

                        </div>
                        <div className="flex flex-col items-center w-[35%]">
                            <div className="relative">
                                <div 
                                    className="w-16 h-16 mb-2 p-3 bg-gray-50 rounded-2xl flex items-center justify-center border border-gray-100 group transition-transform md:hover:scale-110 cursor-pointer shadow-sm md:hover:shadow-md" 
                                    onClick={handleAwayClick}
                                >
                                    <img src={detail.awayTeam.logo} alt="" className="w-full h-full object-contain" />
                                </div>
                                {detail.awayTeam.id && <TeamFavoriteButton teamId={detail.awayTeam.id} lang={lang} onToggle={onToggleFavorite} />}
                            </div>
                            <h3 className="font-black text-xs text-center uppercase tracking-tight line-clamp-2 text-slate-800 h-8 flex items-center cursor-pointer md:hover:text-rose-600" onClick={handleAwayClick}>{getTeamName(detail.awayTeam.id, lang, detail.awayTeam.name)}</h3>
                            <TeamFormStrip form={awayForm} />
                        </div>
                    </div>
                    
                    {!isScheduled && detail.events && (
                        <div className="mt-5 grid grid-cols-2 gap-4">
                            <div className="text-left space-y-1">
                                {detail.events.filter(e => e.type === 'goal' && e.team === 'home').map((e, i) => { 
                                    const isOG = (e.detail?.toLowerCase() || '').includes('own goal'); 
                                    return (
                                        <div key={i} className="flex items-center gap-2 text-[10px] text-slate-500 font-bold">
                                            <SoccerBallIcon className="w-2.5 h-2.5" isOwnGoal={isOG} />
                                            <span className={isOG ? 'text-rose-600' : ''}>{e.player} {e.minute}'{isOG ? ` (${t(lang, 'ownGoal')})` : ''}</span>
                                        </div>
                                    ); 
                                })}
                            </div>
                            <div className="text-right space-y-1">
                                {detail.events.filter(e => e.type === 'goal' && e.team === 'away').map((e, i) => { 
                                    const isOG = (e.detail?.toLowerCase() || '').includes('own goal'); 
                                    return (
                                        <div key={i} className="flex items-center justify-end gap-2 text-[10px] text-slate-500 font-bold">
                                            <span className={isOG ? 'text-rose-600' : ''}>{e.player} {e.minute}'{isOG ? ` (${t(lang, 'ownGoal')})` : ''}</span>
                                            <SoccerBallIcon className="w-2.5 h-2.5" isOwnGoal={isOG} />
                                        </div>
                                    ); 
                                })}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
